import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Coffee, Croissant, UtensilsCrossed, Wheat, CakeSlice, Leaf, Citrus, CalendarHeart, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const PILLARS = [
  {
    icon: Coffee,
    title: "Brunch",
    text: "Portiamo il brunch dove vuoi tu. A casa tua, nel giardino del B&B, in terrazza. Apparecchiamo noi, sparecchiamo noi.",
    to: "/brunch",
    cta: "Scopri il brunch",
    id: "brunch",
  },
  {
    icon: Croissant,
    title: "Bottega",
    text: "Prodotti fatti a mano, da ordinare e portare a casa. Pasta fresca, dolci, conserve: quello che c'è, c'è. Quello che non c'è, si può fare.",
    to: "/bottega",
    cta: "Scopri la Bottega",
    id: "bottega",
  },
  {
    icon: UtensilsCrossed,
    title: "Eventi",
    text: "Piccoli eventi, catering e occasioni private. Compleanni, feste tra amici, aziende che vogliono mangiare bene senza fare i seri.",
    to: "/eventi",
    cta: "Scopri gli eventi",
    id: "eventi",
  },
];

const PRODUCTS = [
  { icon: Wheat, name: "Pasta fresca", text: "Tagliatelle, ravioli e gnocchi tirati a mano." },
  { icon: CakeSlice, name: "Torte e dolci", text: "Da colazione, da compleanno, da scusa qualsiasi." },
  { icon: Citrus, name: "Conserve", text: "Confetture e sott'oli che seguono la stagione." },
  { icon: Leaf, name: "Vegetariano & vegano", text: "La nostra zona di comfort. E si sente." },
  { icon: CalendarHeart, name: "Creazioni stagionali", text: "Quello che il mercato ispira, in barattolo o in teglia." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "FoodEstablishment",
  name: "Brunch & Bottega",
  description: "Brunch a domicilio, eventi privati e prodotti artigianali fatti a mano a Rimini.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Rimini",
    addressRegion: "RN",
    addressCountry: "IT",
  },
  servesCuisine: ["Brunch", "Vegetariano", "Vegano", "Cucina artigianale"],
};

export default function Home() {
  useSeo({
    title: "Brunch & Bottega | Brunch a domicilio e prodotti artigianali a Rimini",
    description: "Brunch a domicilio, catering privati e prodotti fatti a mano a Rimini. Brunch & Bottega: cose buone, preparate con cura.",
    path: "/",
    jsonLd: JSONLD,
  });

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="hero-section">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Rimini · Cucina artigianale</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink">
            Brunch, fatto <em className="text-forest">come si deve</em>.
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-stone-600 max-w-xl">
            Brunch a domicilio, eventi privati e cose buone fatte a mano. A Rimini.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/brunch" data-testid="hero-cta-brunch"
              className="inline-flex justify-center items-center gap-2 bg-forest hover:bg-forest-dark text-cream font-medium px-7 py-3.5 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95">
              Prenota il tuo brunch <ArrowRight size={17} aria-hidden="true" />
            </Link>
            <Link to="/bottega" data-testid="hero-cta-bottega"
              className="inline-flex justify-center items-center gap-2 border border-forest text-forest hover:bg-forest/10 font-medium px-7 py-3.5 rounded-full transition-all active:scale-95">
              Scopri la Bottega
            </Link>
          </div>
        </motion.div>
        <motion.div className="lg:col-span-6 relative" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-forest/10 border border-stone-200/60">
            <img
              src="/images/hero-brunch.jpg"
              alt="Tavola imbandita per un brunch con pane, dolci fatti in casa e caffè"
              className="w-full h-72 sm:h-96 lg:h-[480px] object-cover"
              fetchPriority="high"
              data-testid="hero-image"
            />
          </div>
          <div className="hidden sm:block absolute -bottom-8 -left-8 w-44 rounded-2xl overflow-hidden border-4 border-cream shadow-lg rotate-[-4deg]">
            <img
              src="/images/pasta-hand.jpg"
              alt="Pasta fresca fatta a mano"
              className="w-full h-32 object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>
      </section>

      <section className="bg-sand/70 border-y border-stone-200/60" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">Cosa facciamo</motion.p>
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink max-w-2xl">
            Tre modi per mangiare bene, uno solo per annoiarsi: altrove.
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
            {PILLARS.map((p, i) => (
              <motion.article key={p.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.12 }}
                className="bg-white/80 backdrop-blur-sm border border-stone-200/80 rounded-2xl p-7 shadow-sm hover:shadow-md hover:border-forest/30 hover:-translate-y-1 transition-all"
                data-testid={`pillar-card-${p.id}`}>
                <p.icon size={30} className="text-terracotta" aria-hidden="true" />
                <h3 className="font-display text-xl sm:text-2xl mt-5 text-ink">{p.title}</h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-stone-600">{p.text}</p>
                <Link to={p.to} data-testid={`pillar-link-${p.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-forest hover:text-terracotta transition-colors">
                  {p.cta} <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="concept-section">
        <motion.div {...fadeUp} className="lg:col-span-5 order-2 lg:order-1">
          <div className="rounded-3xl overflow-hidden shadow-lg border border-stone-200/60">
            <img
              src="/images/bottega-shelves.jpg"
              alt="Scaffali di bottega con barattoli di conserve e prodotti artigianali"
              className="w-full h-80 lg:h-[440px] object-cover hover:scale-[1.02] transition-transform duration-700"
              loading="lazy"
              data-testid="concept-image"
            />
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7 order-1 lg:order-2">
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">Il progetto</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.2] text-ink">
            Una bottega. Un brunch. <em className="text-forest">Zero buffet tristi.</em>
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-stone-600 max-w-2xl">
            <p>
              Brunch & Bottega è un piccolo progetto di cucina nato a Rimini. Non siamo un ristorante,
              non abbiamo tavoli né un menu plastificato. Cuciniamo dove vuoi tu e prepariamo
              cose buone da portare a casa.
            </p>
            <p>
              Da una parte il brunch a domicilio e gli eventi privati: arriviamo, cuciniamo,
              apparecchiamo e lasciamo tutto in ordine. Dall'altra la bottega: pasta fresca,
              dolci, conserve e salse fatte a mano, in piccoli lotti, con ingredienti di stagione.
            </p>
            <p>
              Cuciniamo soprattutto vegetariano e vegano, perché ci viene naturale.
              Il resto lo fanno la farina, le uova e una certa testardaggine romagnola.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="bg-forest text-cream" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">Dalla bottega</motion.p>
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">
            Cose buone, in piccoli lotti. Quando finiscono, finiscono.
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {PRODUCTS.map((p, i) => (
              <motion.div key={p.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="bg-cream/5 border border-cream/15 rounded-2xl p-6 hover:bg-cream/10 transition-colors"
                data-testid={`product-teaser-${i}`}>
                <p.icon size={26} className="text-terracotta" aria-hidden="true" />
                <h3 className="font-display text-lg mt-4">{p.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-cream/70">{p.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-12">
            <Link to="/bottega" data-testid="products-cta-bottega"
              className="inline-flex items-center gap-2 bg-cream text-forest font-medium px-7 py-3.5 rounded-full hover:bg-white transition-all shadow-sm active:scale-95">
              Vedi tutta la Bottega <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center" data-testid="cta-section">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
          Hai in mente qualcosa? <em className="text-forest">Parliamone.</em>
        </motion.h2>
        <motion.p {...fadeUp} className="mt-4 text-base text-stone-600 max-w-xl mx-auto">
          Un brunch, una torta, una cena tra amici o un'idea che ancora non ha nome. Scrivici e vediamo che si può fare.
        </motion.p>
        <motion.div {...fadeUp} className="mt-8">
          <Link to="/contatti" data-testid="cta-parliamone"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white font-medium px-8 py-4 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95">
            Parliamone <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </motion.div>
      </section>
    </>
  );
}
